/**
 * NarrationScriptCard - Script-centric card for narration view
 *
 * Design principles (Industry Research - Descript, Premiere Pro, etc.):
 * - Script as center of gravity (80% visual weight)
 * - Full script visible (3-5 lines), not truncated
 * - Small slide number badge (48px) as reference only
 * - Inline audio controls adjacent to each script
 * - Typography optimized for readability (15-16px, 1.5 line height)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import {
  Play,
  Pause,
  Loader2,
  CheckCircle2,
  Circle,
  Sparkles,
  RefreshCw,
  Pencil,
  Check,
  Wand2,
} from 'lucide-react';

export type AudioStatus = 'none' | 'pending' | 'generating' | 'aligning' | 'completed' | 'failed';

/** Progress state for apply operations */
export interface ApplyProgress {
  thinking?: string;
  currentOperation?: string;
  percentage?: number;
}

export interface NarrationScriptCardProps {
  slideId: string;
  slideNumber: number;
  title: string;
  scriptText: string | null;
  thumbnailUrl?: string | null;
  audioStatus: AudioStatus;
  audioProgress?: number;
  audioDuration?: number;
  audioVersion?: number;
  isSelected: boolean;
  isPlaying?: boolean;
  isForceGenerating?: boolean;
  /** Whether audio is being applied (syncing animations) */
  isApplying?: boolean;
  /** Progress state for apply operation */
  applyProgress?: ApplyProgress;
  onSelect: () => void;
  onPlayAudio: () => void;
  onGenerateAudio: () => void;
  onScriptSave?: (text: string) => void;
  isSavingScript?: boolean;
  /** Compact mode for narrower panels (280px) */
  compact?: boolean;
  className?: string;
}

export const NarrationScriptCard: React.FC<NarrationScriptCardProps> = ({
  slideId: _slideId,
  slideNumber,
  title,
  scriptText,
  audioStatus,
  audioProgress,
  audioDuration,
  audioVersion: _audioVersion,
  isSelected,
  isPlaying,
  isForceGenerating,
  isApplying,
  applyProgress,
  onSelect,
  onPlayAudio,
  onGenerateAudio,
  onScriptSave,
  isSavingScript,
  compact = false,
  className,
}) => {
  const hasScript = !!scriptText;
  const wordCount = scriptText ? scriptText.split(/\s+/).filter(Boolean).length : 0;
  const estimatedDuration = Math.round(wordCount / 2.17);

  const effectiveAudioStatus: AudioStatus = isForceGenerating ? 'generating' : audioStatus;
  const isGenerating = effectiveAudioStatus === 'generating' || effectiveAudioStatus === 'aligning';
  const isCompleted = effectiveAudioStatus === 'completed';
  const isFailed = effectiveAudioStatus === 'failed';

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(scriptText || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset edit text when script changes
  useEffect(() => {
    setEditText(scriptText || '');
    setIsEditing(false);
  }, [scriptText]);

  // Auto-resize textarea
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.focus();
    }
  }, [isEditing, editText]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}:${String(secs).padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  const handleSaveScript = useCallback(() => {
    if (onScriptSave && editText !== scriptText) {
      onScriptSave(editText);
    }
    setIsEditing(false);
  }, [editText, scriptText, onScriptSave]);

  const handleCancelEdit = useCallback(() => {
    setEditText(scriptText || '');
    setIsEditing(false);
  }, [scriptText]);

  const handleStartEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleCardClick = useCallback(() => {
    if (!isEditing) {
      onSelect();
    }
  }, [isEditing, onSelect]);

  const editWordCount = editText ? editText.split(/\s+/).filter(Boolean).length : 0;

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "group relative rounded-lg transition-all duration-200",
        "bg-white dark:bg-zinc-900/80",
        "border border-gray-200 dark:border-zinc-800",
        "hover:border-gray-300 dark:hover:border-zinc-700",
        "hover:shadow-md hover:shadow-gray-200/30 dark:hover:shadow-zinc-900/30",
        !isEditing && "cursor-pointer",
        isSelected && [
          "border-primary/30",
          "ring-1 ring-primary/20",
          "bg-accent/30 dark:bg-primary/5"
        ],
        className
      )}
    >
      <div className={cn("p-3", compact && "p-2")}>
        {/* Header: Two-row layout for better responsiveness */}
        {/* Row 1: Slide Badge + Title (truncated) */}
        <div className={cn("flex items-center gap-2 mb-1.5", compact && "mb-1")}>
          {/* Slide number badge - compact gradient */}
          <div className={cn(
            "flex-shrink-0 rounded-md flex items-center justify-center text-primary-foreground font-semibold shadow-sm bg-primary",
            compact ? "w-5 h-5 text-[9px]" : "w-6 h-6 text-[10px]"
          )}>
            {slideNumber}
          </div>

          {/* Title - takes remaining space, truncates */}
          <h3 className={cn(
            "font-medium text-gray-900 dark:text-zinc-100 truncate flex-1 min-w-0",
            compact ? "text-[11px]" : "text-xs"
          )}>
            {title || `Slide ${slideNumber}`}
          </h3>

          {/* Edit button - always visible on title row */}
          {hasScript && onScriptSave && !isEditing && (
            <button
              onClick={handleStartEdit}
              className={cn(
                "flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors",
                isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              )}
              title="Edit script"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Row 2: Meta + Audio Status + Controls - always visible */}
        <div className={cn("flex items-center gap-1.5 mb-2", compact && "mb-1.5")}>
          {/* Word count + duration */}
          {hasScript && !isEditing && (
            <span className="text-[10px] text-gray-400 dark:text-zinc-500 whitespace-nowrap">
              {wordCount}w · ~{estimatedDuration}s
            </span>
          )}

          {/* Spacer to push controls right */}
          <div className="flex-1" />

          {/* Audio status badge */}
          {isCompleted && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-2.5 h-2.5" />
              {audioDuration ? formatDuration(audioDuration) : 'Ready'}
            </span>
          )}
          {isGenerating && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 animate-pulse">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              {effectiveAudioStatus === 'aligning' ? 'Align' : `${audioProgress ?? 0}%`}
            </span>
          )}
          {isFailed && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400">
              <Circle className="w-2.5 h-2.5" />
              Fail
            </span>
          )}
          {!isCompleted && !isGenerating && !isFailed && hasScript && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:text-zinc-500">
              —
            </span>
          )}

          {/* Action buttons - always visible */}
          <div className="flex items-center gap-1">
            {/* Play button */}
            {isCompleted && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayAudio();
                }}
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center transition-colors",
                  isPlaying
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent dark:bg-primary/20 text-primary dark:text-accent-foreground hover:bg-accent dark:hover:bg-primary/30"
                )}
                title="Play audio"
              >
                {isPlaying ? (
                  <Pause className="w-3 h-3" />
                ) : (
                  <Play className="w-3 h-3 ml-0.5" />
                )}
              </button>
            )}

            {/* Regenerate button */}
            {isCompleted && hasScript && !isGenerating && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateAudio();
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center text-primary hover:bg-accent dark:hover:bg-primary/20 transition-colors"
                title="Regenerate audio"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            )}

            {/* Generate button */}
            {!isCompleted && hasScript && !isGenerating && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateAudio();
                }}
                className="w-6 h-6 rounded-full flex items-center justify-center text-primary hover:bg-accent dark:hover:bg-primary/20 transition-colors"
                title="Generate audio"
              >
                <Sparkles className="w-3 h-3" />
              </button>
            )}

            {/* Generating spinner */}
            {isGenerating && (
              <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <Loader2 className="w-3 h-3 text-amber-600 dark:text-amber-400 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Script Content - Aligned with title */}
        <div className={cn("pl-8", compact && "pl-7")}>
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                ref={textareaRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "w-full text-[13px] leading-relaxed resize-none rounded-lg p-3 min-h-[80px]",
                  "bg-gray-50 dark:bg-zinc-800/50",
                  "border border-gray-200 dark:border-zinc-700",
                  "outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30",
                  "text-gray-700 dark:text-zinc-300",
                  "placeholder:text-gray-400 dark:placeholder:text-zinc-500",
                  "transition-all duration-200"
                )}
                placeholder="Enter script text..."
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400 dark:text-zinc-500">
                  {editWordCount}w · ~{Math.round(editWordCount / 2.17)}s
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEdit();
                    }}
                    disabled={isSavingScript}
                    className="h-6 px-2 text-[11px] text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveScript();
                    }}
                    disabled={isSavingScript || editText === scriptText}
                    className="h-6 px-2.5 text-[11px] font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded disabled:opacity-50 transition-colors flex items-center gap-1"
                  >
                    {isSavingScript ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Check className="w-3 h-3" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : hasScript ? (
            <p className={cn(
              "text-[13px] leading-relaxed text-gray-600 dark:text-zinc-400 whitespace-pre-wrap",
              compact && "text-xs"
            )}>
              {scriptText}
            </p>
          ) : (
            <p className="text-[12px] text-gray-400 dark:text-zinc-500 italic">
              No script yet. Generate scripts to add narration.
            </p>
          )}
        </div>

        {/* Apply Progress Indicator - shows during Apply All */}
        {isApplying && applyProgress && (
          <div className={cn("mt-2 pl-8", compact && "pl-7")}>
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-primary/30 bg-accent">
              <Wand2 className="w-3.5 h-3.5 text-primary dark:text-accent-foreground animate-pulse flex-shrink-0" />
              <p className="flex-1 text-[11px] text-accent-foreground truncate">
                {applyProgress.thinking || applyProgress.currentOperation || 'Syncing...'}
              </p>
              {applyProgress.percentage !== undefined && applyProgress.percentage > 0 && applyProgress.percentage < 100 && (
                <span className="text-[10px] text-primary dark:text-accent-foreground tabular-nums flex-shrink-0">
                  {applyProgress.percentage}%
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NarrationScriptCard;
